import { useState } from 'react';
import { api } from '../api';
import { useDroits } from '../auth';
import { useAction, useApi } from '../hooks';
import { Champ, Chargement, MessageErreur, Modal, Statut, formatDate } from '../components/ui';

const formatTaille = (octets) => {
  if (octets === null || octets === undefined) return '—';
  if (octets < 1024 * 1024) return `${Math.ceil(octets / 1024)} Ko`;
  return `${(octets / 1024 / 1024).toFixed(1)} Mo`;
};

export default function Logiciels() {
  const { admin, superAdmin } = useDroits();
  const logiciels = useApi('/logiciels');
  const [selection, setSelection] = useState(null);
  const [modal, setModal] = useState(null);

  if (logiciels.erreur) return <MessageErreur erreur={logiciels.erreur} />;
  if (!logiciels.donnees) return <Chargement />;
  const choisi = logiciels.donnees.find((l) => l.id === selection) || logiciels.donnees[0];

  return (
    <>
      <div className="titre-page">
        <h1>Logiciels</h1>
        {superAdmin && (
          <button className="btn btn-secondaire" onClick={() => setModal({ type: 'logiciel' })}>
            + Nouveau logiciel
          </button>
        )}
      </div>

      <div className="carte">
        <table>
          <thead>
            <tr>
              <th>Code</th>
              <th>Nom</th>
              <th>Dernière version</th>
              <th>Versions</th>
              <th>Postes équipés</th>
              <th>Statut</th>
              {superAdmin && <th />}
            </tr>
          </thead>
          <tbody>
            {logiciels.donnees.map((l) => (
              <tr
                key={l.id}
                className={`ligne-cliquable ${choisi?.id === l.id ? 'ligne-choisie' : ''}`}
                onClick={() => setSelection(l.id)}
              >
                <td>{l.code}</td>
                <td>{l.name}</td>
                <td>{l.derniereVersion || '—'}</td>
                <td>{l.nombreVersions}</td>
                <td>{l.postesEquipes}</td>
                <td>
                  <Statut valeur={l.status} />
                </td>
                {superAdmin && (
                  <td>
                    <button
                      className="btn btn-lien"
                      onClick={(e) => {
                        e.stopPropagation();
                        setModal({ type: 'logiciel', logiciel: l });
                      }}
                    >
                      Modifier
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {choisi && (
        <Versions key={choisi.id} logiciel={choisi} peutActualiser={admin} onChange={logiciels.recharger} />
      )}

      {modal?.type === 'logiciel' && (
        <FormulaireLogiciel
          logiciel={modal.logiciel}
          onFermer={() => setModal(null)}
          onEnregistre={() => {
            setModal(null);
            logiciels.recharger();
          }}
        />
      )}
    </>
  );
}

function Versions({ logiciel, peutActualiser, onChange }) {
  const versions = useApi('/versions', { softwareId: logiciel.id });
  const action = useAction();

  // Relit tout de suite les manifest.json du serveur de mises à jour
  const actualiser = () =>
    action.executer(async () => {
      await api('/versions/synchroniser', { methode: 'POST' });
      versions.recharger();
      onChange();
    });

  return (
    <section className="carte">
      <div className="titre-page">
        <h2>Versions de {logiciel.name}</h2>
        {peutActualiser && (
          <button className="btn btn-secondaire" disabled={action.enCours} onClick={actualiser}>
            {action.enCours ? 'Lecture…' : 'Relire le manifeste'}
          </button>
        )}
      </div>
      <p className="muted">Versions lues dans le manifest.json du serveur de mises à jour.</p>
      <MessageErreur erreur={action.erreur || versions.erreur} />
      {versions.donnees && (
        <table>
          <thead>
            <tr>
              <th>Version</th>
              <th>Date</th>
              <th>Taille</th>
              <th>Postes</th>
              <th>Notes</th>
            </tr>
          </thead>
          <tbody>
            {versions.donnees.map((v) => (
              <tr key={v.id}>
                <td>
                  {v.version} {v.isLatest && <span className="badge badge-vert">Dernière</span>}
                </td>
                <td>{formatDate(v.releaseDate)}</td>
                <td>{formatTaille(v.fileSize === null ? null : Number(v.fileSize))}</td>
                <td>{v._count.installedOn}</td>
                <td className="notes-version">{v.releaseNotes || '—'}</td>
              </tr>
            ))}
            {versions.donnees.length === 0 && (
              <tr>
                <td colSpan={5} className="muted">
                  Aucune version dans le manifeste.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      )}
    </section>
  );
}

function FormulaireLogiciel({ logiciel, onFermer, onEnregistre }) {
  const { executer, enCours, erreur } = useAction();
  const [valeurs, setValeurs] = useState({
    code: logiciel?.code || '',
    name: logiciel?.name || '',
    description: logiciel?.description || '',
    status: logiciel?.status || 'ACTIVE',
  });
  const champ = (cle) => ({ value: valeurs[cle], onChange: (e) => setValeurs({ ...valeurs, [cle]: e.target.value }) });

  const soumettre = (e) => {
    e.preventDefault();
    executer(async () => {
      if (logiciel) await api(`/logiciels/${logiciel.id}`, { methode: 'PUT', corps: valeurs });
      else await api('/logiciels', { methode: 'POST', corps: valeurs });
      onEnregistre();
    });
  };

  return (
    <Modal titre={logiciel ? `Modifier ${logiciel.name}` : 'Nouveau logiciel'} onFermer={onFermer}>
      <form onSubmit={soumettre}>
        <MessageErreur erreur={erreur} />
        <Champ libelle="Code *" aide="Identifiant technique : lettres, chiffres et _">
          <input {...champ('code')} required />
        </Champ>
        <Champ libelle="Nom *">
          <input {...champ('name')} required />
        </Champ>
        <Champ libelle="Description">
          <input {...champ('description')} />
        </Champ>
        <Champ libelle="Statut">
          <select {...champ('status')}>
            <option value="ACTIVE">Actif</option>
            <option value="INACTIVE">Inactif</option>
          </select>
        </Champ>
        <div className="actions-modal">
          <button type="button" className="btn btn-secondaire" onClick={onFermer}>
            Annuler
          </button>
          <button className="btn btn-principal" disabled={enCours}>
            Enregistrer
          </button>
        </div>
      </form>
    </Modal>
  );
}
